import { apiClient } from "@/services/apiClient";
import type { ProfessionalTotpState } from "@/services/professionalTotpService";

export interface AccountantUser {
  /**
   * Tri-state, not a boolean: "declared, cannot issue CFE" is a DIFFERENT
   * situation from "never declared" and needs a different response from an
   * admin (spec 82f26c86).
   */
  fiscalStatus: "complete" | "cannot_issue_cfe" | "not_declared";
  id: string;
  email: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  profileImage: string | null;
  status: string;
  accountantActivationStatus: "pending" | "active" | null;
  /**
   * Active assignments this professional holds across BOTH request tables,
   * against `activeAssignmentLimit`. Computed by the server — the cap is one
   * number for the person, not per discipline, and a surface that counted one
   * table would report half of it.
   */
  activeAssignments: number;
  activeAssignmentLimit: number;
  pendingReviewInviteId: string | null;
  createdAt: string;
}

export interface AccountantProfileDetail {
  /**
   * The fiscal declaration, read-only (spec 82f26c86). The activation gate
   * reads exactly these fields, so this is where an admin finds out why a
   * professional cannot be activated.
   */
  fiscalStatus: "complete" | "cannot_issue_cfe" | "not_declared";
  rut: string | null;
  razonSocial: string | null;
  issuesCfe: boolean | null;
  cfeProvider: string | null;
  id: string;
  email: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  secondName: string | null;
  secondLastName: string | null;
  profileImage: string | null;
  status: string;
  accountantActivationStatus: "pending" | "active" | null;
  pendingReviewInviteId: string | null;
  createdAt: string;
  emailVerifiedAt: string | null;
  identityVerifiedAt: string | null;
  cedula: string | null;
  invitedByUserId: string | null;
  invitedByEmail: string | null;
  inviteAcceptedAt: string | null;
}

export interface AccountantRequestHistoryItem {
  id: string;
  requestedByUserId: string;
  applicantName: string | null;
  applicantEmail: string | null;
  status: string;
  requestType: string;
  acceptedAt: string | null;
  updatedAt: string;
  documentCount: number;
}

export interface AccountantDetailResponse {
  profile: AccountantProfileDetail;
  requestHistory: {
    items: AccountantRequestHistoryItem[];
    total: number;
    page: number;
    limit: number;
  };
  /**
   * Second-factor state. MUST stay identical to NotaryDetailResponse's block —
   * a professional's discipline cannot decide whether their 2FA is serviceable
   * (spec b8731a82, FM-3), and one shared card renders both.
   */
  totp: ProfessionalTotpState;
}

export interface PendingAccountantInvite {
  id: string;
  email: string;
  invitedByUserId: string;
  invitedByEmail: string | null;
  expiresAt: string;
  createdAt: string;
  expired: boolean;
  manualApprovalStatus: "pending" | "approved" | "declined" | null;
  identityVerifiedAt: string | null;
  professionalCertificateUploadedAt: string | null;
  awaitingManualReview: boolean;
}

export function listAccountants(): Promise<AccountantUser[]> {
  return apiClient.get<AccountantUser[]>("/platform/accountants").then((res) => res.data);
}

export function getAccountantDetail(
  accountantId: string,
  params?: { requestsPage?: number; requestsLimit?: number }
): Promise<AccountantDetailResponse> {
  const searchParams = new URLSearchParams();
  if (params?.requestsPage != null) {
    searchParams.set("requestsPage", String(params.requestsPage));
  }
  if (params?.requestsLimit != null) {
    searchParams.set("requestsLimit", String(params.requestsLimit));
  }
  const qs = searchParams.toString();
  return apiClient
    .get<AccountantDetailResponse>(
      `/platform/accountants/${encodeURIComponent(accountantId)}${qs ? `?${qs}` : ""}`
    )
    .then((res) => res.data);
}

export function listPendingAccountantInvites(): Promise<PendingAccountantInvite[]> {
  return apiClient
    .get<PendingAccountantInvite[]>("/platform/accountant-invites")
    .then((res) => res.data);
}

export function inviteAccountant(email: string): Promise<void> {
  return apiClient
    .post("/auth/accountant-invite", { email: email.trim().toLowerCase() })
    .then(() => undefined);
}

export function resendAccountantInvite(id: string): Promise<void> {
  return apiClient.post(`/platform/accountant-invites/${id}/resend`).then(() => undefined);
}

export function cancelAccountantInvite(id: string): Promise<void> {
  return apiClient.delete(`/platform/accountant-invites/${id}`).then(() => undefined);
}

export function deleteAccountant(userId: string): Promise<void> {
  return apiClient
    .delete<{ ok: true }>(`/platform/accountants/${userId}`)
    .then(() => undefined);
}

export interface AccountantInviteManualReviewDetail {
  id: string;
  email: string;
  invitedByUserId: string;
  invitedByEmail: string | null;
  validatedFirstName: string | null;
  validatedSecondFirstName: string | null;
  validatedLastName: string | null;
  validatedSecondLastName: string | null;
  validatedCedula: string | null;
  professionalCertificateUrl: string | null;
  professionalCertificateUploadedAt: string | null;
  identityVerifiedAt: string | null;
  manualApprovalStatus: "pending" | "approved" | "declined" | null;
  manualApprovalAt: string | null;
  manualDeclineReason: string | null;
  expiresAt: string;
  createdAt: string;
  expired: boolean;
}

export function getAccountantInviteManualReviewDetail(
  inviteId: string
): Promise<AccountantInviteManualReviewDetail> {
  return apiClient
    .get<AccountantInviteManualReviewDetail>(
      `/platform/accountant-invites/${encodeURIComponent(inviteId)}/manual-review-detail`
    )
    .then((res) => res.data);
}

export function fetchAccountantInviteProfessionalCertificate(inviteId: string): Promise<Blob> {
  return apiClient
    .get(`/platform/accountant-invites/${encodeURIComponent(inviteId)}/professional-certificate`, {
      responseType: "blob",
    })
    .then((res) => res.data as Blob);
}

export function approveAccountantInviteManualReview(inviteId: string): Promise<void> {
  return apiClient
    .post(`/platform/accountant-invites/${encodeURIComponent(inviteId)}/manual-approve`)
    .then(() => undefined);
}

export function declineAccountantInviteManualReview(
  inviteId: string,
  body?: { reason?: string }
): Promise<void> {
  return apiClient
    .post(`/platform/accountant-invites/${encodeURIComponent(inviteId)}/manual-decline`, body ?? {})
    .then(() => undefined);
}
